import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Grid, Card, CardContent, Stack, Button, Divider, List, ListItem, ListItemText, Chip, Accordion, AccordionSummary, AccordionDetails, Paper, LinearProgress, Tooltip, Avatar } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import LockIcon from '@mui/icons-material/Lock';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PaperCard from '../components/PaperCard';
import { APP_DOWNLOAD } from '../config/appConfig';
import { httpGet, httpPost } from '../lib/http';
import { useAuth } from '../context/AuthContext';
import { useStudyPlans } from '../context/StudyPlansContext';
import seed from '../data/studyPathsSeedDetailed';

export default function SubjectView() {
  const { pathId, subjectId } = useParams();
  const [path, setPath] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(null);
  const { isAuthenticated, user } = useAuth();
  const { getTrack } = useStudyPlans();
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        // Prefer cached track from StudyPlansContext to avoid extra API calls
        const cached = getTrack(pathId);
        if (cached && (Array.isArray(cached.orderedSubjects) || Array.isArray(cached.subjects))) {
          const ordered = Array.isArray(cached.orderedSubjects) ? cached.orderedSubjects : cached.subjects || [];
          setPath({ ...cached, orderedSubjects: ordered });
          setProgress(cached.progress || null);
          if (mounted) setLoading(false);
          return;
        }

        const res = await httpGet(`/api/study-paths/${encodeURIComponent(pathId)}`);
        if (res && res.ok) {
          const data = await res.json();
          if (!mounted) return;
          setPath(data);
        } else {
          const seedPath = seed.find(s => s.id === pathId);
          setPath(seedPath || null);
        }

        if (isAuthenticated && user) {
          try {
            const userId = user?.id || user?.userId || user?._id;
            const p = await httpGet(`/api/users/${encodeURIComponent(userId)}/path-progress/${encodeURIComponent(pathId)}`);
            if (p && p.ok) {
              const pdata = await p.json();
              setProgress(pdata);
            } else {
              setProgress(null);
            }
          } catch (e) {
            setProgress(null);
          }
        }
      } catch (e) {
        const seedPath = seed.find(s => s.id === pathId);
        setPath(seedPath || null);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [pathId, subjectId, isAuthenticated, user]);

  if (loading) return <Box sx={{ mt: 8, textAlign: 'center' }}>Loading…</Box>;
  if (!path) return <Container sx={{ mt: 8 }}><Typography>No study path found.</Typography></Container>;

  const subject = path.orderedSubjects.find(s => s.id === subjectId || s.title === subjectId);
  if (!subject) return <Container sx={{ mt: 8 }}><Typography>Subject not found.</Typography></Container>;

  function topicCompleted(topicId) {
    if (!progress) return false;
    try {
      const tp = (progress.topicProgress || []).find(t => t.topicId === topicId);
      return Boolean(tp && tp.lessonCompleted);
    } catch {
      return false;
    }
  }

  const chaptersList = Array.isArray(subject?.chapters) ? subject.chapters : (Array.isArray(subject?.topics) ? [{ id: 'legacy', title: subject.title || 'Topics', topics: subject.topics }] : []);
  const allTopics = chaptersList.reduce((acc, ch) => acc.concat(Array.isArray(ch.topics) ? ch.topics : []), []);
  const subjectCompleted = allTopics.length ? allTopics.every(t => topicCompleted(t.id || t.title)) : false;

  async function markTopicComplete(topicId) {
    if (!isAuthenticated || !user) {
      navigate(`/login?from=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    setMarking(topicId);
    try {
      const userId = user?.id || user?.userId || user?._id;
      const res = await httpPost(`/api/users/${encodeURIComponent(userId)}/path-progress/${encodeURIComponent(pathId)}/topic/${encodeURIComponent(topicId)}/complete`, {});
      if (res && res.ok) {
        const updated = await res.json();
        setProgress(updated);
      } else {
        // attempt to refresh progress via GET
        const p = await httpGet(`/api/users/${encodeURIComponent(userId)}/path-progress/${encodeURIComponent(pathId)}`);
        if (p && p.ok) setProgress(await p.json());
      }
    } catch (e) {
      // ignore
    } finally {
      setMarking(null);
    }
  }

  function startTopic(topicId) {
    navigate(`/study-paths/${pathId}/subject/${subjectId}/topic/${topicId}`);
  }

  function startPaper(paper) {
    if (!subjectCompleted) return;
    // Navigate to existing exam flow — use pathId as examId for seeded data
    navigate(`/exam/${pathId}/paper/${paper.paperId}`);
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Enhanced Header */}
      <Paper sx={{ 
        p: 4, 
        mb: 4, 
        borderRadius: '16px', 
        background: 'radial-gradient(circle at 70% 30%, rgba(45, 91, 255, 0.05) 0%, rgba(255, 255, 255, 0) 50%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: 'text.primary'
      }}>
  <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
                <Typography variant="h3" fontWeight={900} sx={{ letterSpacing: '-0.02em', color: 'primary.dark' }}>
                  {subject.name}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 500 }}>
                  {subject.description}
                </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                <Chip 
                  label={`${subject.topicCount || 0} Topics`}
                  sx={{ 
                    bgcolor: 'rgba(45, 91, 255, 0.08)', 
                    color: '#2D5BFF',
                    fontWeight: 700,
                    borderRadius: '16px',
                    textTransform: 'uppercase',
                    fontSize: '0.7rem'
                  }}
                />
                <Chip 
                  label={`${subject.estimatedHours || 0} Hours`}
                  sx={{ 
                    bgcolor: 'rgba(45, 91, 255, 0.08)', 
                    color: '#2D5BFF',
                    fontWeight: 700,
                    borderRadius: '16px',
                    textTransform: 'uppercase',
                    fontSize: '0.7rem'
                  }}
                />
                {subjectCompleted && (
                  <Chip 
                    icon={<CheckCircleIcon sx={{ color: 'white !important' }} />}
                    label="Mastered"
                    sx={{ 
                      bgcolor: 'rgba(76, 175, 80, 0.4)', 
                      color: 'white',
                      fontWeight: 800,
                      borderRadius: '16px',
                      textTransform: 'uppercase',
                      fontSize: '0.7rem'
                    }}
                  />
                )}
              </Stack>
            </Box>
              <Button 
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/subject-practice')}
                sx={{ 
                  color: '#2D5BFF',
                  borderColor: 'rgba(45, 91, 255, 0.3)',
                  borderRadius: '16px',
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 3,
                  '&:hover': {
                    borderColor: '#2D5BFF',
                    bgcolor: 'rgba(45, 91, 255, 0.05)'
                  }
                }}
                variant="outlined"
              >
                Back to Subjects
              </Button>
          </Box>

          {/* Progress Bar */}
          {allTopics.length > 0 && (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight={600} sx={{ opacity: 0.95 }}>
                  Subject Progress
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {Math.round((allTopics.filter(t => topicCompleted(t.id || t.title)).length / allTopics.length) * 100)}%
                </Typography>
              </Stack>
              <LinearProgress 
                variant="determinate" 
                value={(allTopics.filter(t => topicCompleted(t.id || t.title)).length / allTopics.length) * 100}
                sx={{ 
                  height: 10,
                  borderRadius: '16px',
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: 'white'
                  }
                }}
              />
            </Box>
          )}
        </Stack>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              📖 Chapters & Topics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Expand each chapter to see topics. Complete topics in sequence to track your progress!
            </Typography>
          </Box>

          <Stack spacing={2}>
            {chaptersList.length === 0 ? (
              <Card sx={{ 
                borderRadius: '16px',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none'
              }}>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <Avatar sx={{ 
                    width: 80, 
                    height: 80, 
                    mx: 'auto', 
                    mb: 2,
                    bgcolor: 'action.hover' 
                  }}>
                    <MenuBookIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                  </Avatar>
                  <Typography color="text.secondary" variant="h6">
                    No chapters available yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Content will be added soon. Stay tuned!
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              chaptersList.map((ch, ci) => {
                const chapterTopics = Array.isArray(ch.topics) ? ch.topics : [];
                const chapterCompleted = chapterTopics.every(t => topicCompleted(t.id || t.title));
                const chapterProgress = chapterTopics.length > 0 
                  ? (chapterTopics.filter(t => topicCompleted(t.id || t.title)).length / chapterTopics.length) * 100 
                  : 0;

                return (
                  <Accordion 
                    key={ch.id || ch.title}
                    sx={{ 
                      borderRadius: '16px',
                      border: '1px solid',
                      borderColor: chapterCompleted ? 'success.main' : 'divider',
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        margin: 0,
                        mb: 2,
                        border: '1px solid',
                        borderColor: 'primary.main'
                      },
                      mb: 2,
                      overflow: 'hidden'
                    }}
                    disableGutters
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ 
                        px: 3,
                        py: 2,
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                          <Avatar sx={{ 
                            bgcolor: chapterCompleted ? 'success.main' : 'primary.main',
                            width: 48,
                            height: 48,
                            borderRadius: '16px'
                          }}>
                            {chapterCompleted ? (
                              <CheckCircleIcon sx={{ color: 'white' }} />
                            ) : (
                              <Typography variant="h6" fontWeight={900}>
                                {ci + 1}
                              </Typography>
                            )}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" fontWeight={700}>
                              {ch.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {chapterTopics.length} topics • {chapterTopics.reduce((s, t) => s + (t.estimatedMinutes || 0), 0)} mins
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {chapterCompleted ? (
                            <Chip 
                              icon={<CheckCircleIcon />}
                              label="Complete" 
                              color="success" 
                              size="small"
                              sx={{ fontWeight: 800, borderRadius: '16px', textTransform: 'uppercase', fontSize: '0.65rem' }}
                            />
                          ) : (
                            <Chip 
                              label={`${Math.round(chapterProgress)}%`}
                              color="primary"
                              size="small"
                              sx={{ fontWeight: 800, borderRadius: '16px', textTransform: 'uppercase', fontSize: '0.65rem' }}
                            />
                          )}
                        </Stack>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pt: 0, pb: 2 }}>
                      <Divider />
                      <List sx={{ width: '100%', pt: 2 }}>
                        {chapterTopics.map((t, ti) => {
                          const tid = t.id || t.title || `${ch.id || ch.title}-${ti}`;
                          const isCompleted = topicCompleted(tid);
                          
                          return (
                            <ListItem 
                              key={tid} 
                              sx={{ 
                                px: 3,
                                py: 2,
                                borderLeft: '4px solid',
                                borderLeftColor: isCompleted ? 'success.main' : 'transparent',
                                bgcolor: isCompleted ? 'success.lighter' : 'transparent',
                                '&:hover': {
                                  bgcolor: isCompleted ? 'success.light' : 'action.hover',
                                  borderLeftColor: isCompleted ? 'success.dark' : 'primary.main',
                                },
                                transition: 'all 0.2s ease',
                                mb: 0.5
                              }}
                            >
                              <Avatar sx={{ 
                                mr: 2.5,
                                bgcolor: isCompleted ? 'success.main' : 'action.hover',
                                width: 36,
                                height: 36,
                                borderRadius: '16px'
                              }}>
                                {isCompleted ? (
                                  <CheckCircleIcon sx={{ fontSize: 20, color: 'white' }} />
                                ) : (
                                  <Typography variant="caption" fontWeight={900}>
                                    {ti + 1}
                                  </Typography>
                                )}
                              </Avatar>
                              <ListItemText 
                                sx={{ flex: 1, minWidth: 0 }} 
                                primary={
                                  <Typography fontWeight={600}>
                                    {t.title}
                                  </Typography>
                                }
                                secondary={
                                  <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }}>
                                    <Chip 
                                      label={`${t.practiceCount || 0} Practice`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ 
                                        height: 22,
                                        fontSize: '0.65rem',
                                        borderRadius: '16px',
                                        fontWeight: 700,
                                        textTransform: 'uppercase'
                                      }}
                                    />
                                    <Chip 
                                      label={`${t.estimatedMinutes || 0} Mins`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ 
                                        height: 22,
                                        fontSize: '0.65rem',
                                        borderRadius: '16px',
                                        fontWeight: 700,
                                        textTransform: 'uppercase'
                                      }}
                                    />
                                  </Stack>
                                }
                                primaryTypographyProps={{ noWrap: false }} 
                              />
                              <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
                                <Tooltip title="Start learning this topic">
                                  <Button 
                                    size="small" 
                                    variant="outlined"
                                    startIcon={<PlayCircleFilledIcon />}
                                    onClick={() => startTopic(tid)}
                                    sx={{ 
                                      borderRadius: '16px',
                                      fontWeight: 700,
                                      minWidth: 100,
                                      textTransform: 'none'
                                    }}
                                  >
                                    {isCompleted ? 'Review' : 'Start'}
                                  </Button>
                                </Tooltip>
                                {!isCompleted && (
                                  <Tooltip title="Mark this topic as completed">
                                    <Button 
                                      size="small" 
                                      variant="contained"
                                      disabled={Boolean(marking)}
                                      onClick={() => markTopicComplete(tid)}
                                      sx={{ 
                                        borderRadius: '16px',
                                        fontWeight: 700,
                                        minWidth: 130,
                                        textTransform: 'none'
                                      }}
                                    >
                                      {marking === tid ? 'Marking...' : 'Mark Complete'}
                                    </Button>
                                  </Tooltip>
                                )}
                                {isCompleted && (
                                  <Chip 
                                    icon={<CheckCircleIcon />}
                                    label="Done" 
                                    size="small" 
                                    color="success"
                                    sx={{ 
                                      fontWeight: 800,
                                      height: 32,
                                      borderRadius: '16px',
                                      textTransform: 'uppercase',
                                      fontSize: '0.7rem'
                                    }}
                                  />
                                )}
                              </Stack>
                            </ListItem>
                          );
                        })}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                );
              })
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ position: 'sticky', top: 80 }}>
            <Card sx={{ 
              borderRadius: '16px',
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <Typography variant="h6" fontWeight={700}>
                    📄 Previous Year Papers
                  </Typography>
                  {!subjectCompleted && (
                    <Tooltip title="Complete all topics to unlock">
                      <LockIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    </Tooltip>
                  )}
                </Stack>
                <Divider sx={{ mb: 2 }} />
                {subject.previousPapers && subject.previousPapers.length ? (
                  <Stack spacing={2}>
                    {subject.previousPapers.map((p, idx) => (
                      <PaperCard
                        key={p.paperId}
                        paper={{ id: p.paperId, title: p.title, year: p.year }}
                        index={idx}
                        variant="previous"
                        onClick={() => startPaper(p)}
                        showAppDownloadQr={APP_DOWNLOAD.enabled}
                        playStoreUrl={APP_DOWNLOAD.playStoreUrl}
                        qrImageUrl={APP_DOWNLOAD.qrImageUrl}
                        qrSize={APP_DOWNLOAD.qrSize}
                        disabled={!subjectCompleted}
                        actionButtonText={subjectCompleted ? 'Start Paper' : 'Locked'}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 4,
                    bgcolor: 'action.hover',
                    borderRadius: '16px'
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      📝 No previous papers available yet
                    </Typography>
                  </Box>
                )}
                {!subjectCompleted && subject.previousPapers && subject.previousPapers.length > 0 && (
                  <Box sx={{ 
                    mt: 3, 
                    p: 2, 
                    bgcolor: 'warning.lighter',
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'warning.light'
                  }}>
                    <Typography variant="caption" sx={{ color: 'warning.dark', fontWeight: 600 }}>
                      🔒 Complete all topics to unlock practice papers!
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
}
